import authService from './authService';
import {
  SalesOrder,
  SalesOrderSearchModel,
  SplitOrderPayload,
  CloneOrderPayload,
  VolumePayload,
  VolumeItem,
  VolumeItemsPayload,
  ItemPayload,
  InstallmentPayload,
  ShippingAddressPayload,
  SalesOrderCreatePayload,
  OrderPaymentMethod,
  Seller,
  Customer,
  OrderFreightQuote,
  FreightQuoteSelectPayload,
  OrderStatusOption,
  ReplacementOrderItem,
  ReplacementOrderItemPayload,
  OrderChangeEntry,
} from '../types/salesOrder';
import { PaginatedResponse, ApiResponse } from '../types/common';
import { apiErrorFrom } from '../utils/apiError';
import type { CardPaymentsResponse, CardRefund, CardRefundRequest, CardRefundResult } from '../types/cardRefund';
import type { OrderAddressReviewView } from '../types/addressReview';

// ─── Payload for updating customer on an existing order ─────────

export interface UpdateOrderCustomerPayload {
  customer_id: number;
  ship_addr_street?: string;
  ship_addr_num?: string;
  ship_addr_compl?: string;
  ship_addr_district?: string;
  ship_addr_city?: string;
  ship_addr_state?: string;
  ship_addr_zip?: string;
}

interface UpdateOrderOptions {
  includePaymentFields?: boolean;
  /**
   * Confirmação explícita, dada pelo usuário na modal, de que as parcelas do pedido podem
   * ser recalculadas junto com o novo total. O backend exige esta flag (409) sempre que a
   * alteração muda o valor total de um pedido que já tem parcelas — ver
   * ERPApi/internal/core/domain/order_installments_recalc.go.
   */
  recalculateInstallments?: boolean;
}

// ─── Service ────────────────────────────────────────────────────

class SalesOrderService {
  private normalizeRequestDeliveryTime(value: unknown): unknown {
    if (value === null || value === undefined || value === '') {
      return value;
    }

    if (typeof value !== 'string') {
      return value;
    }

    // datetime-local input returns "YYYY-MM-DDTHH:mm" (no timezone).
    // Convert directly to "dd/mm/yyyy HH:mm" without any Date parsing to
    // avoid browser timezone shifting (e.g. UTC-3 adding 3h to UTC).
    const localDtMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (localDtMatch) {
      const [, year, month, day, hour, minute] = localDtMatch;
      return `${day}/${month}/${year} ${hour}:${minute}`;
    }

    // Already in Brazilian format — pass through.
    if (/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/.test(value)) {
      return value;
    }

    // Full ISO string with timezone — pass through as-is (already valid for the backend).
    return value;
  }

  private async getApiClient() {
    return authService.getApiClient();
  }

  private async getBaseUrl(): Promise<string> {
    const { getConfig } = await import('../config');
    return getConfig().api.baseUrl;
  }

  // ─── Payment Methods ───────────────────────────────────────

  async getPaymentMethods(): Promise<OrderPaymentMethod[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/payment-methods');
    if (!response.ok) throw new Error('Failed to fetch payment methods');
    const data: ApiResponse<OrderPaymentMethod[]> = await response.json();
    return data.data!;
  }

  // ─── Order Statuses ───────────────────────────────────────

  async getOrderStatuses(): Promise<OrderStatusOption[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/order-statuses');
    if (!response.ok) throw new Error('Failed to fetch order statuses');
    const data: ApiResponse<OrderStatusOption[]> = await response.json();
    return data.data!;
  }

  // ─── Sellers ───────────────────────────────────────────────

  async getSellers(): Promise<Seller[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/orders/sellers?active_only=true');
    if (!response.ok) throw new Error('Failed to fetch sellers');
    const data: ApiResponse<Seller[]> = await response.json();
    return data.data!;
  }
  // ─── List ───────────────────────────────────────────────────

  async getOrders(searchModel: SalesOrderSearchModel): Promise<PaginatedResponse<SalesOrder>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      limit: searchModel.pageSize.toString(),
      ...(searchModel.searchName && { search: searchModel.searchName }),
      ...(searchModel.company_id !== undefined && { company_id: searchModel.company_id.toString() }),
      ...(searchModel.order_status_id !== undefined && { order_status_id: searchModel.order_status_id.toString() }),
      ...(searchModel.order_status && { order_status: searchModel.order_status }),
      ...(searchModel.order_type_id !== undefined && { order_type_id: searchModel.order_type_id.toString() }),
      ...(searchModel.date_from && { date_from: searchModel.date_from }),
      ...(searchModel.date_to && { date_to: searchModel.date_to }),
      ...(searchModel.customer_name && { customer_name: searchModel.customer_name }),
      ...(searchModel.orderBy && { orderBy: searchModel.orderBy }),
      ...(searchModel.orderDirection && { orderDirection: searchModel.orderDirection }),
    });

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/orders?${params}`);
    if (!response.ok) throw new Error('Failed to fetch orders');

    const json = await response.json();

    // API returns { data: [], pagination: { page, page_size, total_items, total_pages }, success }
    const items: SalesOrder[] = Array.isArray(json.data) ? json.data
      : Array.isArray(json.data?.data) ? json.data.data
      : [];
    const pg = json.pagination ?? json.data?.pagination ?? {};

    return {
      success: json.success ?? true,
      data: items,
      pagination: {
        page: pg.page ?? searchModel.page,
        limit: pg.page_size ?? pg.pageSize ?? searchModel.pageSize,
        total: pg.total_items ?? pg.total ?? items.length,
        totalPages: pg.total_pages ?? pg.totalPages ?? (Math.ceil((pg.total_items ?? pg.total ?? items.length) / searchModel.pageSize) || 1),
      },
      // Valor total do filtro inteiro (não só da página): sem desmembrado
      // filho, demonstração/presente e cancelados — regra no backend.
      summary: typeof pg.total_amount === 'number' ? { total_amount_sum: pg.total_amount } : undefined,
    };
  }

  // ─── Get by ID ──────────────────────────────────────────────

  async getOrderById(id: number): Promise<SalesOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/orders/${id}`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Pedido não encontrado');
      throw new Error('Failed to fetch order');
    }
    const data: ApiResponse<SalesOrder> = await response.json();
    console.log(`📦 getOrderById(${id}): volumes=${JSON.stringify(data.data?.volumes?.length ?? 'undefined')}, items=${data.data?.items?.length ?? 'undefined'}, installments=${data.data?.installments?.length ?? 'undefined'}`);
    console.log(`📦 getOrderById(${id}): address/type loaded=`, {
      ship_addr_street: data.data?.ship_addr_street,
      ship_addr_city: data.data?.ship_addr_city,
      order_type_id: data.data?.order_type_id,
      observation: data.data?.observation,
    });
    return data.data!;
  }

  // ─── Análise de endereço (antifraude, pedido de 1ª compra) ───

  async getAddressReview(id: number): Promise<OrderAddressReviewView> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/orders/${id}/address-review`);
    if (!response.ok) throw await apiErrorFrom(response, 'Falha ao carregar a análise de endereço');
    const data: ApiResponse<OrderAddressReviewView> = await response.json();
    return data.data!;
  }

  /** Força a consulta ao Google (Street View). */
  async refreshAddressReview(id: number): Promise<OrderAddressReviewView> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/orders/${id}/address-review/refresh`);
    if (!response.ok) throw await apiErrorFrom(response, 'Falha ao buscar a imagem do endereço');
    const data: ApiResponse<OrderAddressReviewView> = await response.json();
    return data.data!;
  }

  async decideAddressReview(id: number, approve: boolean, note: string): Promise<OrderAddressReviewView> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/orders/${id}/address-review`, { approve, note });
    if (!response.ok) throw await apiErrorFrom(response, 'Falha ao registrar a análise de endereço');
    const data: ApiResponse<OrderAddressReviewView> = await response.json();
    return data.data!;
  }

  /**
   * Imagem da fachada, buscada no Google a cada chamada (não é armazenada — termos do Google).
   * Devolve um object URL; quem chama faz o revokeObjectURL.
   */
  async getAddressReviewImageUrl(id: number, heading?: number): Promise<string> {
    const apiClient = await this.getApiClient();
    const qs = heading === undefined ? '' : `?heading=${heading}`;
    const response = await apiClient.get(`/orders/${id}/address-review/image${qs}`);
    if (!response.ok) throw await apiErrorFrom(response, 'Falha ao carregar a imagem do Street View');
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  /**
   * Anexa (ou troca) a foto/print da portaria ou fachada (condomínio fechado, sem Street View).
   * Multipart (campo "photo"), por isso fetch direto e não o apiClient JSON — mesmo padrão da
   * foto do projeto do produto.
   */
  async uploadAddressReviewPhoto(id: number, photo: Blob, fileName = 'portaria.jpg'): Promise<OrderAddressReviewView> {
    const baseUrl = await this.getBaseUrl();
    const fd = new FormData();
    fd.append('photo', photo, fileName);

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/orders/${id}/address-review/photo`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) throw await apiErrorFrom(response, 'Falha ao anexar a foto');
    const data: ApiResponse<OrderAddressReviewView> = await response.json();
    return data.data!;
  }

  async deleteAddressReviewPhoto(id: number): Promise<OrderAddressReviewView> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/orders/${id}/address-review/photo`);
    if (!response.ok) throw await apiErrorFrom(response, 'Falha ao remover a foto');
    const data: ApiResponse<OrderAddressReviewView> = await response.json();
    return data.data!;
  }

  /** Foto anexada (blob privado, pelo proxy da API). Devolve um object URL; quem chama revoga. */
  async getAddressReviewPhotoUrl(id: number): Promise<string> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/orders/${id}/address-review/photo`);
    if (!response.ok) throw await apiErrorFrom(response, 'Falha ao carregar a foto anexada');
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  // ─── Histórico/auditoria de alterações ──────────────────────

  async getOrderHistory(id: number): Promise<OrderChangeEntry[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/orders/${id}/history`);
    if (!response.ok) {
      if (response.status === 403) throw new Error('Sem permissão para ver o histórico do pedido');
      throw new Error('Falha ao carregar o histórico do pedido');
    }
    const data: ApiResponse<OrderChangeEntry[]> = await response.json();
    return data.data ?? [];
  }

  /**
   * Troca a empresa emissora do pedido. Rota própria (PATCH /orders/:id/company) e NÃO um
   * campo do updateOrder: aquele monta o corpo espalhando o pedido inteiro, então company_id
   * já viaja em toda edição — aceitá-lo lá faria qualquer salvamento mover a empresa.
   *
   * Só passa antes da NF; o backend recalcula os totais, porque imposto e comissão saem de
   * VariableFees, que é por empresa.
   */
  async changeOrderCompany(id: number, companyId: number): Promise<SalesOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(`/orders/${id}/company`, { company_id: companyId });
    if (!response.ok) throw await apiErrorFrom(response, 'Erro ao alterar a empresa do pedido');
    const data: ApiResponse<SalesOrder> = await response.json();
    return data.data!;
  }

  // ─── Update Status ─────────────────────────────────────────

  async updateOrderStatus(id: number, statusId: number): Promise<SalesOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(`/orders/${id}/status`, { order_status_id: statusId });
    if (!response.ok) throw await apiErrorFrom(response, 'Erro ao atualizar status do pedido');
    const data: ApiResponse<SalesOrder> = await response.json();
    return data.data!;
  }

  // ─── Cancel linked commercial proposal ────────────────────

  /**
   * Cancela a proposta comercial vinculada ao pedido de venda.
   * Chamado automaticamente pelo frontend ao cancelar um pedido que possui proposal_num.
   * Endpoint: PATCH /api/orders/:id/cancel-proposal
   */
  async cancelLinkedProposal(orderId: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(`/orders/${orderId}/cancel-proposal`, {});
    if (!response.ok) {
      // Não bloqueia o fluxo de cancelamento se falhar
      console.error('Failed to cancel linked proposal');
    }
  }

  // ─── Clone ─────────────────────────────────────────────────

  async cloneOrder(payload: CloneOrderPayload): Promise<SalesOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/orders/clone', payload);
    if (!response.ok) throw await apiErrorFrom(response, 'Erro ao clonar pedido');
    const data: ApiResponse<SalesOrder> = await response.json();
    return data.data!;
  }

  // ─── Split (Desmembrar) ────────────────────────────────────

  async splitOrder(payload: SplitOrderPayload): Promise<{ order1: SalesOrder; order2: SalesOrder }> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/orders/split', payload);
    if (!response.ok) throw await apiErrorFrom(response, 'Erro ao desmembrar pedido');
    const data: ApiResponse<{ order1: SalesOrder; order2: SalesOrder }> = await response.json();
    return data.data!;
  }

  // ─── Update Order ──────────────────────────────────────────

  /**
   * Atualiza um pedido enviando TODOS os campos escalares para evitar que
   * o backend apague campos não enviados. O `currentOrder` é o estado atual
   * completo e `changes` são os campos a sobrescrever.
   */
  async updateOrder(
    id: number,
    changes: Partial<SalesOrder>,
    currentOrder?: SalesOrder,
    options?: UpdateOrderOptions,
  ): Promise<SalesOrder> {
    const apiClient = await this.getApiClient();

    // Se temos o order atual, envia o payload completo (merge) para evitar que o backend nullifique campos
    let payload: Record<string, unknown>;
    if (currentOrder) {
      const { items, volumes, installments, freight_quotes, replacement_items, child_orders, ...scalarFields } = currentOrder;
      payload = { ...scalarFields, ...changes };
    } else {
      payload = { ...changes };
    }

    const shouldIncludePaymentFields = Boolean(options?.includePaymentFields)
      && (Object.prototype.hasOwnProperty.call(changes, 'is_payed')
        || Object.prototype.hasOwnProperty.call(changes, 'payment_date'));

    if (!shouldIncludePaymentFields) {
      delete payload.is_payed;
      delete payload.payment_date;
    }

    if (Object.prototype.hasOwnProperty.call(payload, 'request_delivery_time')) {
      payload.request_delivery_time = this.normalizeRequestDeliveryTime(payload.request_delivery_time);
    }

    if (options?.recalculateInstallments) {
      payload.recalculate_installments = true;
    }

    console.log(`🔄 updateOrder(${id}): changes=`, changes);
    console.log(`🔄 updateOrder(${id}): currentOrder has address?`, {
      ship_addr_street: currentOrder?.ship_addr_street,
      ship_addr_city: currentOrder?.ship_addr_city,
      order_type_id: currentOrder?.order_type_id,
      observation: currentOrder?.observation,
    });
    console.log(`🔄 updateOrder(${id}): FULL PAYLOAD KEYS=`, Object.keys(payload));
    console.log(`🔄 updateOrder(${id}): payload address fields=`, {
      ship_addr_street: payload.ship_addr_street,
      ship_addr_city: payload.ship_addr_city,
      order_type_id: payload.order_type_id,
      observation: payload.observation,
    });

    const response = await apiClient.patch(`/orders/${id}`, payload);
    // `apiErrorFrom` prioriza `message`: `error` é só o status genérico
    // ("Conflict", "Forbidden") e engoliria a explicação do backend — por
    // exemplo a exigência de confirmar o recálculo das parcelas.
    if (!response.ok) throw await apiErrorFrom(response, 'Erro ao atualizar pedido');
    const data: ApiResponse<SalesOrder> = await response.json();
    console.log(`✅ updateOrder(${id}): RESPONSE address fields=`, {
      ship_addr_street: data.data?.ship_addr_street,
      ship_addr_city: data.data?.ship_addr_city,
      order_type_id: data.data?.order_type_id,
      observation: data.data?.observation,
    });
    return data.data!;
  }

  /**
   * Atualiza somente a observação interna do pedido. Este endpoint dedicado permite
   * editar a observação interna independentemente do status do pedido ou de nota fiscal
   * emitida (usado para registrar eventos do processo de entrega).
   */
  async updateInternalObservation(id: number, internalObservation: string): Promise<SalesOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(`/orders/${id}/internal-observation`, {
      internal_observation: internalObservation,
    });
    if (!response.ok) throw await apiErrorFrom(response, 'Erro ao atualizar observação interna');
    const data: ApiResponse<SalesOrder> = await response.json();
    return data.data!;
  }

  /**
   * Marca/desmarca o follow-up pós-venda do pedido. O horário (followup_done_timestamp) é
   * gravado/limpo automaticamente pelo backend em hora de Brasília. Editável independentemente
   * do status do pedido ou de nota fiscal emitida (perfis admin/finance/seller).
   */
  async updateFollowup(id: number, followupDone: boolean, followupDoneTimestamp?: string | null): Promise<SalesOrder> {
    const apiClient = await this.getApiClient();
    // O timestamp é enviado como wall-clock de Brasília (string do datetime-local, sem
    // conversão para UTC); o backend grava literal. Só envia quando marcado como feito.
    const response = await apiClient.patch(`/orders/${id}/followup`, {
      followup_done: followupDone,
      followup_done_timestamp: followupDone ? (followupDoneTimestamp || null) : null,
    });
    if (!response.ok) throw await apiErrorFrom(response, 'Erro ao atualizar follow-up');
    const data: ApiResponse<SalesOrder> = await response.json();
    return data.data!;
  }

  /**
   * Marca manualmente o pedido como Entregue (finance/admin), informando a data/hora de entrega
   * e um motivo que é anexado (append) à observação interna do pedido. Exige nota fiscal emitida
   * e status pós-conferência (Conferido/Atendido/Enviado).
   *
   * deliveryTimestamp vem do input datetime-local ("YYYY-MM-DDTHH:mm") e é convertido para o
   * formato brasileiro "dd/mm/yyyy HH:mm" (wall-clock de Brasília, sem conversão de fuso) antes
   * de enviar ao backend, reusando normalizeRequestDeliveryTime.
   */
  async markDelivered(id: number, deliveryTimestamp: string, reason: string): Promise<SalesOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(`/orders/${id}/mark-delivered`, {
      delivery_timestamp: this.normalizeRequestDeliveryTime(deliveryTimestamp),
      reason,
    });
    if (!response.ok) throw await apiErrorFrom(response, 'Erro ao marcar pedido como entregue');
    const data: ApiResponse<SalesOrder> = await response.json();
    return data.data!;
  }

  /**
   * Estorna o lançamento de estoque do pedido de venda (estorno de estoque).
   * Disponível apenas para admin/finance e somente quando o pedido não possui nota
   * fiscal vinculada e o estoque foi lançado.
   */
  async reverseStock(id: number): Promise<SalesOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/orders/${id}/reverse-stock`, {});
    if (!response.ok) throw await apiErrorFrom(response, 'Erro ao estornar estoque');
    const data: ApiResponse<SalesOrder> = await response.json();
    return data.data!;
  }

  // ─── Estorno de cartão (PaymentHub → Cielo) ───────────────

  /** Vendas de cartão do pedido com o saldo estornável (somente admin). */
  async getCardPayments(id: number): Promise<CardPaymentsResponse> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/orders/${id}/card-payments`);
    if (!response.ok) throw await apiErrorFrom(response, 'Erro ao consultar os pagamentos de cartão');
    const data: ApiResponse<CardPaymentsResponse> = await response.json();
    return data.data!;
  }

  /** Estorna total ou parcialmente uma venda de cartão do pedido (somente admin). */
  async refundCard(id: number, req: CardRefundRequest): Promise<CardRefundResult> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/orders/${id}/card-refunds`, req);
    if (!response.ok) throw await apiErrorFrom(response, 'Erro ao estornar o cartão');
    const data: ApiResponse<CardRefundResult> = await response.json();
    return data.data!;
  }

  /** Estornos de cartão do pedido (quem lê o pedido). */
  async listCardRefunds(id: number): Promise<CardRefund[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/orders/${id}/card-refunds`);
    if (!response.ok) throw await apiErrorFrom(response, 'Erro ao listar os estornos de cartão');
    const data: ApiResponse<CardRefund[]> = await response.json();
    return data.data ?? [];
  }

  /** Baixa o comprovante de cancelamento (PDF) de um estorno. */
  async downloadCardRefundPdf(id: number, refundId: number): Promise<void> {
    await this.downloadBlob(`/orders/${id}/card-refunds/${refundId}/pdf`, `comprovante-estorno-${id}-${refundId}.pdf`, 'Erro ao gerar o comprovante');
  }

  private async downloadBlob(path: string, fallbackName: string, errorPrefix: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}${path}`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || `${errorPrefix} (${response.status})`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? fallbackName;

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  // ─── PDF ───────────────────────────────────────────────────

  async downloadPdf(id: number): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/orders/${id}/pdf`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || `Erro ao gerar PDF (${response.status})`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? `pedido-venda-${id}.pdf`;

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  // ─── Shipping Label ─────────────────────────────────────────

  async downloadShippingLabel(id: number): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/orders/${id}/shipping-labels`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || `Erro ao gerar etiqueta de envio (${response.status})`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? `etiqueta-pedido-${id}.pdf`;

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  // ─── Volume CRUD ─────────────────────────────────────────────────────

  async addVolume(orderId: number, payload: VolumePayload): Promise<SalesOrder> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.post(`/orders/${orderId}/volumes`, payload);
    if (!res.ok) throw await apiErrorFrom(res, 'Erro ao adicionar volume');
    const data: ApiResponse<SalesOrder> = await res.json();
    return data.data!;
  }

  async updateVolume(orderId: number, volumeId: number, payload: VolumePayload): Promise<SalesOrder> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.put(`/orders/${orderId}/volumes/${volumeId}`, payload);
    if (!res.ok) throw await apiErrorFrom(res, 'Erro ao atualizar volume');
    const data: ApiResponse<SalesOrder> = await res.json();
    return data.data!;
  }

  async deleteVolume(orderId: number, volumeId: number): Promise<SalesOrder> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.delete(`/orders/${orderId}/volumes/${volumeId}`);
    if (!res.ok) throw await apiErrorFrom(res, 'Erro ao excluir volume');
    const data: ApiResponse<SalesOrder> = await res.json();
    return data.data!;
  }

  async setVolumeItems(orderId: number, volumeId: number, payload: VolumeItemsPayload): Promise<VolumeItem[]> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.put(`/orders/${orderId}/volumes/${volumeId}/items`, payload);
    if (!res.ok) throw await apiErrorFrom(res, 'Erro ao salvar itens do volume');
    const data: ApiResponse<VolumeItem[]> = await res.json();
    return data.data ?? [];
  }

  // ─── Item CRUD ──────────────────────────────────────────────────────

  /**
   * Alterar itens muda o valor total do pedido. Quando o pedido já tem parcelas, o backend
   * exige `recalculate_installments` (o "sim" da modal) e a permissão de alterar parcelas.
   */
  async addItem(orderId: number, payload: ItemPayload, recalculateInstallments?: boolean): Promise<SalesOrder> {
    const apiClient = await this.getApiClient();
    const body = recalculateInstallments ? { ...payload, recalculate_installments: true } : payload;
    const res = await apiClient.post(`/orders/${orderId}/items`, body);
    if (!res.ok) throw await apiErrorFrom(res, 'Erro ao adicionar item');
    const data: ApiResponse<SalesOrder> = await res.json();
    return data.data!;
  }

  async updateItem(orderId: number, itemId: number, payload: ItemPayload, recalculateInstallments?: boolean): Promise<SalesOrder> {
    const apiClient = await this.getApiClient();
    const body = recalculateInstallments ? { ...payload, recalculate_installments: true } : payload;
    const res = await apiClient.put(`/orders/${orderId}/items/${itemId}`, body);
    if (!res.ok) throw await apiErrorFrom(res, 'Erro ao atualizar item');
    const data: ApiResponse<SalesOrder> = await res.json();
    return data.data!;
  }

  async deleteItem(orderId: number, itemId: number, recalculateInstallments?: boolean): Promise<SalesOrder> {
    const apiClient = await this.getApiClient();
    // DELETE não tem corpo: a confirmação vai na query string.
    const suffix = recalculateInstallments ? '?recalculate_installments=true' : '';
    const res = await apiClient.delete(`/orders/${orderId}/items/${itemId}${suffix}`);
    if (!res.ok) throw await apiErrorFrom(res, 'Erro ao excluir item');
    const data: ApiResponse<SalesOrder> = await res.json();
    return data.data!;
  }

  // ─── Recalculate Order ──────────────────────────────────────────────

  async recalculateOrder(orderId: number): Promise<SalesOrder> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.post(`/orders/${orderId}/recalculate`, {});
    if (!res.ok) throw await apiErrorFrom(res, 'Erro ao recalcular pedido');
    const data: ApiResponse<SalesOrder> = await res.json();
    return data.data!;
  }

  // ─── Batch PDF ──────────────────────────────────────────────────────

  /**
   * PDF de vários pedidos num arquivo só (POST /orders/pdf-batch, até 50 por lote).
   * Com `targetWindow` (aba aberta no clique, antes do await, para o navegador não bloquear),
   * o PDF abre nela pronto para imprimir; sem ela, o arquivo é baixado.
   */
  async downloadBatchPdf(orderIds: number[], targetWindow?: Window | null, filenameSuffix = ''): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/orders/pdf-batch`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ order_ids: orderIds }),
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || `Erro ao gerar PDF em lote (${response.status})`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = (filenameMatch?.[1] ?? `pedidos-lote.pdf`).replace(/\.pdf$/i, `${filenameSuffix}.pdf`);

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    if (targetWindow && !targetWindow.closed) {
      targetWindow.location.href = url;
      // A aba ainda está lendo o PDF: libera a URL só depois.
      setTimeout(() => URL.revokeObjectURL(url), 120_000);
      return;
    }
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  // ─── Installment CRUD ───────────────────────────────────────────────

  async addInstallment(orderId: number, payload: InstallmentPayload): Promise<SalesOrder> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.post(`/orders/${orderId}/installments`, payload);
    if (!res.ok) throw await apiErrorFrom(res, 'Erro ao adicionar parcela');
    const data: ApiResponse<SalesOrder> = await res.json();
    return data.data!;
  }

  async updateInstallment(orderId: number, installmentId: number, payload: InstallmentPayload): Promise<SalesOrder> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.put(`/orders/${orderId}/installments/${installmentId}`, payload);
    if (!res.ok) throw await apiErrorFrom(res, 'Erro ao atualizar parcela');
    const data: ApiResponse<SalesOrder> = await res.json();
    return data.data!;
  }

  async deleteInstallment(orderId: number, installmentId: number): Promise<SalesOrder> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.delete(`/orders/${orderId}/installments/${installmentId}`);
    if (!res.ok) throw await apiErrorFrom(res, 'Erro ao excluir parcela');
    const data: ApiResponse<SalesOrder> = await res.json();
    return data.data!;
  }

  // ─── Shipping Address ──────────────────────────────────────────────

  async updateShippingAddress(orderId: number, payload: ShippingAddressPayload): Promise<SalesOrder> {
    const apiClient = await this.getApiClient();
    console.log(`🏠 updateShippingAddress(${orderId}): payload=`, payload);
    const res = await apiClient.patch(`/orders/${orderId}/address`, payload);
    if (!res.ok) throw await apiErrorFrom(res, 'Erro ao atualizar endereço');
    const data: ApiResponse<SalesOrder> = await res.json();
    console.log(`✅ updateShippingAddress(${orderId}): RESPONSE=`, {
      ship_addr_street: data.data?.ship_addr_street,
      order_type_id: data.data?.order_type_id,
      observation: data.data?.observation,
    });
    return data.data!;
  }

  // ─── Create Order ──────────────────────────────────────────────

  async createOrder(payload: SalesOrderCreatePayload): Promise<SalesOrder> {
    const normalized: SalesOrderCreatePayload = { ...payload };
    if (normalized.request_delivery_time) {
      normalized.request_delivery_time = this.normalizeRequestDeliveryTime(normalized.request_delivery_time) as string;
    }
    const apiClient = await this.getApiClient();
    const res = await apiClient.post('/orders', normalized);
    if (!res.ok) throw await apiErrorFrom(res, 'Erro ao criar pedido');
    const data: ApiResponse<SalesOrder> = await res.json();
    return data.data!;
  }

  // ─── Search Customers ─────────────────────────────────────────

  async searchCustomers(query: string): Promise<Customer[]> {
    const params = new URLSearchParams({ search: query, limit: '10' });
    const apiClient = await this.getApiClient();
    const res = await apiClient.get(`/customers?${params}`);
    if (!res.ok) throw new Error('Erro ao buscar clientes');
    const data: ApiResponse<Customer[]> = await res.json();
    return data.data ?? [];
  }

  // ─── Update Order Customer ────────────────────────────────────

  async updateOrderCustomer(orderId: number, payload: UpdateOrderCustomerPayload): Promise<SalesOrder> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.patch(`/orders/${orderId}/customer`, payload);
    if (!res.ok) throw await apiErrorFrom(res, 'Erro ao atualizar cliente do pedido');
    const data: ApiResponse<SalesOrder> = await res.json();
    return data.data!;
  }

  // ─── Lookup by number (quick navigation) ─────────────────────

  /**
   * Busca o OrderId de um pedido de venda pelo campo selecionado pelo usuário.
   * Endpoint: GET /api/orders/lookup?q=<query>&type=<erp|ecommerce|id>
   *   - erp       → busca por OrderNumERP (exato)
   *   - ecommerce → busca por OrderNumEcommerce (exato, case-insensitive)
   *   - id        → busca por OrderId (exato)
   * Retorna 404 se nenhum pedido for encontrado.
   */
  async lookupOrder(query: string, type: 'erp' | 'ecommerce' | 'id' = 'erp'): Promise<number> {
    const apiClient = await this.getApiClient();
    const params = new URLSearchParams({ q: query.trim(), type });
    const response = await apiClient.get(`/orders/lookup?${params}`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Pedido não encontrado');
      throw new Error('Erro ao buscar pedido');
    }
    const data: ApiResponse<{ order_id: number }> = await response.json();
    return data.data!.order_id;
  }

  // ─── Freight Quotes ─────────────────────────────────────────────
  //
  // Estes quatro usam apiErrorFrom, e não `new Error(...)`, porque o backend distingue causas que
  // o usuário precisa ver: CEP ausente (400), status que bloqueia (409), permissão (403), falha do
  // FreightHub (500). Trocar tudo por uma frase única fazia o operador preencher o CEP, continuar
  // vendo "Erro ao calcular cotações de frete" e não ter como saber que a causa já era outra.

  async getFreightQuotes(orderId: number): Promise<OrderFreightQuote[]> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.get(`/orders/${orderId}/freight-quotes`);
    if (!res.ok) throw await apiErrorFrom(res, 'Erro ao buscar cotações de frete');
    const data: ApiResponse<OrderFreightQuote[]> = await res.json();
    return data.data ?? [];
  }

  async calculateFreightQuotes(orderId: number): Promise<OrderFreightQuote[]> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.post(`/orders/${orderId}/freight-quotes/calculate`, {});
    if (!res.ok) throw await apiErrorFrom(res, 'Erro ao calcular cotações de frete');
    const data: ApiResponse<OrderFreightQuote[]> = await res.json();
    return data.data ?? [];
  }

  async selectFreightQuote(orderId: number, payload: FreightQuoteSelectPayload): Promise<OrderFreightQuote[]> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.patch(`/orders/${orderId}/freight-quotes/select`, payload);
    if (!res.ok) throw await apiErrorFrom(res, 'Erro ao selecionar cotação de frete');
    const data: ApiResponse<OrderFreightQuote[]> = await res.json();
    return data.data ?? [];
  }

  async deleteFreightQuotes(orderId: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.delete(`/orders/${orderId}/freight-quotes`);
    if (!res.ok) throw await apiErrorFrom(res, 'Erro ao excluir cotações de frete');
  }

  // ─── Replacement Items (Retirada para Troca) ────────────────────

  async getReplacementItems(orderId: number): Promise<ReplacementOrderItem[]> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.get(`/orders/${orderId}/replacement-items`);
    if (!res.ok) throw new Error('Erro ao buscar itens de troca');
    const data: ApiResponse<ReplacementOrderItem[]> = await res.json();
    return data.data ?? [];
  }

  async addReplacementItems(orderId: number, items: ReplacementOrderItemPayload[]): Promise<ReplacementOrderItem[]> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.post(`/orders/${orderId}/replacement-items`, { items });
    if (!res.ok) throw new Error('Erro ao adicionar itens de troca');
    const data: ApiResponse<ReplacementOrderItem[]> = await res.json();
    return data.data ?? [];
  }

  async deleteReplacementItem(orderId: number, replacementItemId: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.delete(`/orders/${orderId}/replacement-items/${replacementItemId}`);
    if (!res.ok) throw new Error('Erro ao remover item de troca');
  }

  // ─── Customer Orders (para modal de troca) ──────────────────────

  async getCustomerOrders(customerId: number, excludeOrderId?: number): Promise<SalesOrder[]> {
    const apiClient = await this.getApiClient();
    const pageSize = 100;
    const allOrders: SalesOrder[] = [];
    const seenIds = new Set<number>();
    const maxPages = 200;

    for (let page = 1; page <= maxPages; page += 1) {
      const params = new URLSearchParams({
        customer_id: String(customerId),
        page: String(page),
        limit: String(pageSize),
        page_size: String(pageSize),
        order_by: 'order_date',
        order_direction: 'desc',
      });

      if (excludeOrderId) params.set('exclude_order_id', String(excludeOrderId));

      const res = await apiClient.get(`/orders?${params}`);
      if (!res.ok) throw new Error('Erro ao buscar pedidos do cliente');

      const json = await res.json();
      const payload = json.data ?? json;
      const pageOrders: SalesOrder[] = Array.isArray(payload?.data)
        ? payload.data
        : (Array.isArray(payload) ? payload : []);

      let addedInPage = 0;
      for (const order of pageOrders) {
        const orderId = Number(order.order_id);
        if (!Number.isFinite(orderId) || !seenIds.has(orderId)) {
          allOrders.push(order);
          addedInPage += 1;
          if (Number.isFinite(orderId)) seenIds.add(orderId);
        }
      }

      const pg = json.pagination ?? payload?.pagination ?? {};
      const totalPagesRaw = pg.total_pages ?? pg.totalPages;
      const currentPageRaw = pg.page;
      const responsePageSizeRaw = pg.page_size ?? pg.pageSize ?? pg.limit;
      const totalPages = Number(totalPagesRaw);
      const currentPage = Number(currentPageRaw || page);
      const responsePageSize = Number(responsePageSizeRaw || pageSize);

      if (Number.isFinite(totalPages) && totalPages > 0) {
        if (currentPage >= totalPages) break;
        continue;
      }

      if (pageOrders.length === 0 || pageOrders.length < responsePageSize || addedInPage === 0) break;
    }

    return allOrders;
  }

  // ─── Delivery Details ─────────────────────────────────────────

  async getDeliveryDetails(orderId: number): Promise<DeliveryDetails> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.get(`/orders/${orderId}/delivery-details`);
    if (!res.ok) throw new Error('Erro ao buscar detalhes da entrega');
    const data: ApiResponse<DeliveryDetails> = await res.json();
    return data.data!;
  }

  async getDeliveryPhotoUrl(orderId: number, photoIndex: 1 | 2): Promise<string> {
    const baseUrl = await this.getBaseUrl();
    return `${baseUrl}/orders/${orderId}/delivery-photos/${photoIndex}`;
  }
}

export interface DeliveryDetails {
  delivery_contact: string;
  delivery_contact_document?: string;
  delivered_at: string;
  evidence_photo_1_url?: string | null;
  evidence_photo_2_url?: string | null;
}

// Singleton instance
export const salesOrderService = new SalesOrderService();
export default salesOrderService;
